"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Menu,
  Message,
  Modal,
  Segment,
  TextArea,
} from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";

import CustomerPicker, {
  CUSTOM_TARGET_LIMIT,
} from "../../../components/CustomerPicker";
import { slashAppBroadcasts } from "../../../utils/slashAppBroadcasts";
import { PREFECTURES } from "../../../constants/studioConstants";
import InstructorAnnouncementForm from "./InstructorAnnouncementForm";

const TARGET_TYPES = {
  ALL: "all_customers",
  STUDIO: "studio",
  CUSTOM: "custom",
  RECITAL2027: "recital2027",
};

const MESSAGE_TYPE_OPTIONS = [
  { key: "text", text: "テキスト", value: "text" },
  { key: "image", text: "画像", value: "image" },
  { key: "video", text: "動画", value: "video" },
];

// bubble の order を 0..N-1 で振り直して返す。
function reindex(bubbles) {
  return bubbles.map((b, i) => ({ ...b, order: i }));
}

function groupStudiosByPrefecture(studios) {
  return studios.reduce((acc, s) => {
    const key = s.prefecture || "未設定";
    if (!acc[key]) acc[key] = [];
    acc[key].push(s);
    return acc;
  }, {});
}

function sortByPrefectureOrder(grouped) {
  const out = {};
  PREFECTURES.forEach((p) => {
    if (grouped[p.text]) out[p.text] = grouped[p.text];
  });
  // PREFECTURES に無いものを後ろに足す。
  Object.keys(grouped).forEach((key) => {
    if (!out[key]) out[key] = grouped[key];
  });
  return out;
}

function BroadcastMessageEditPage() {
  const navigate = useNavigate();
  const { id: editingId } = useParams(); // 既存(下書き)を編集する場合に id が入る
  const [searchParams] = useSearchParams();

  // ---- 配信チャネル (会員 / インストラクター) -------------------------
  // インストラクター宛ては会員向け一斉送信 (吹き出し/チャット) とは別系統で、
  // タイトル + 本文のお知らせとして配信する (InstructorAnnouncementForm)。
  // 編集時は一覧から audience=instructor が付与されるので、その場合は
  // チャネルを固定してインストラクター編集フォームを開く。
  const [channel, setChannel] = useState(
    searchParams.get("audience") === "instructor" ? "instructor" : "customer"
  );
  const channelLocked = !!editingId; // 編集中はチャネル切替不可

  // ---- 配信対象 -------------------------------------------------------
  const [targetType, setTargetType] = useState(TARGET_TYPES.ALL);
  const [targetStudios, setTargetStudios] = useState([]); // number[]
  const [targetCustomers, setTargetCustomers] = useState([]); // number[]
  const [sortedStudios, setSortedStudios] = useState({});
  const [studioLoadError, setStudioLoadError] = useState(null);

  // 発表会2027 タブの配信対象 (会員ID) は slash_admin の解決APIから取得する。
  // 送信時はこの ids を target_type='custom' で slash-app に渡す。
  const [recitalTargets, setRecitalTargets] = useState({
    loading: false,
    ids: [],
    count: 0,
    recipients: [],
    error: null,
    loaded: false,
  });
  // slash-app の custom 上限 (BROADCAST_CUSTOM_LIMIT) と揃える。
  const RECITAL_TARGET_LIMIT = 5000;

  // 全顧客/店舗/顧客指定 の配信先プレビュー（送信確認ダイアログ用）。
  // 送信確認を開いたタイミングで取得する。
  const [targetPreview, setTargetPreview] = useState({
    loading: false,
    recipients: [],
    count: 0,
    error: null,
  });

  // ---- 吹き出し ------------------------------------------------------
  const [bubbles, setBubbles] = useState([
    { key: Date.now(), order: 0, message_type: "text", text: "", s3_url: "", thumbnail_url: "" },
  ]);
  const [uploading, setUploading] = useState({}); // {key: bool}

  // ---- モーダル / 状態 ----------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // ---- 店舗データ取得 (slash_admin Django backend を流用) -----------
  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/get_studios/`);
        if (!res.ok) throw new Error("店舗データの取得に失敗しました");
        const data = await res.json();
        setSortedStudios(sortByPrefectureOrder(groupStudiosByPrefecture(data)));
      } catch (e) {
        setStudioLoadError(e.message || "店舗データの取得に失敗しました");
      }
    };
    fetchStudios();
  }, []);

  // ---- 既存(下書き)の読み込み -------------------------------------
  // インストラクター編集は InstructorAnnouncementForm が自前で読み込むため、
  // 会員チャネルのときだけ broadcasts を読む。
  useEffect(() => {
    if (!editingId || channel !== "customer") return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await slashAppBroadcasts.detail(editingId);
        if (cancelled) return;
        setTargetType(data.target_type || TARGET_TYPES.ALL);
        setTargetStudios(data.target_studios || []);
        setTargetCustomers(data.target_customers || []);
        const loaded = (data.bubbles || []).map((b, i) => ({
          key: `loaded-${b.id != null ? b.id : i}`,
          order: typeof b.order === "number" ? b.order : i,
          message_type: b.message_type || "text",
          text: b.text || "",
          s3_url: b.s3_url || "",
          thumbnail_url: b.thumbnail_url || "",
        }));
        if (loaded.length > 0) setBubbles(loaded);
      } catch (e) {
        if (!cancelled) setError(e.message || "下書きの読み込みに失敗しました");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [editingId, channel]);

  // ---- 発表会2027 対象者の解決 (slash_admin API) -------------------
  useEffect(() => {
    if (targetType !== TARGET_TYPES.RECITAL2027) return;
    let cancelled = false;
    const fetchTargets = async () => {
      setRecitalTargets((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(
          `${apiUrl}/api/get_recital2027_broadcast_targets/`
        );
        if (!res.ok) throw new Error("発表会2027の対象者取得に失敗しました");
        const data = await res.json();
        if (cancelled) return;
        setRecitalTargets({
          loading: false,
          ids: data.customer_ids || [],
          count: data.count || 0,
          recipients: data.recipients || [],
          error: null,
          loaded: true,
        });
      } catch (e) {
        if (cancelled) return;
        setRecitalTargets({
          loading: false,
          ids: [],
          count: 0,
          recipients: [],
          error: e.message || "発表会2027の対象者取得に失敗しました",
          loaded: true,
        });
      }
    };
    fetchTargets();
    return () => {
      cancelled = true;
    };
  }, [targetType]);

  // ---- bubble 編集 ---------------------------------------------------
  const addBubble = () => {
    setBubbles((prev) =>
      reindex([
        ...prev,
        {
          key: Date.now() + Math.random(),
          order: prev.length,
          message_type: "text",
          text: "",
          s3_url: "",
          thumbnail_url: "",
        },
      ])
    );
    setDirty(true);
  };

  const removeBubble = (key) => {
    setBubbles((prev) => reindex(prev.filter((b) => b.key !== key)));
    setDirty(true);
  };

  const updateBubble = (key, patch) => {
    setBubbles((prev) =>
      prev.map((b) => (b.key === key ? { ...b, ...patch } : b))
    );
    setDirty(true);
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    // ファイルサイズの簡易チェック (画像 20MB / 動画 50MB)。
    if (file.type.startsWith("image/") && file.size > 20 * 1024 * 1024) {
      alert("画像は 20MB 以下にしてください。");
      return;
    }
    if (file.type.startsWith("video/") && file.size > 50 * 1024 * 1024) {
      alert("動画は 50MB 以下にしてください。");
      return;
    }
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await slashAppBroadcasts.upload(file);
      updateBubble(key, {
        s3_url: res.s3_url,
        thumbnail_url: res.thumbnail_url || "",
      });
    } catch (e) {
      alert(e.message || "アップロードに失敗しました");
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // ---- 店舗チェック -------------------------------------------------
  const toggleStudio = (id) => {
    setTargetStudios((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
    setDirty(true);
  };

  // ---- バリデーション -----------------------------------------------
  const validation = useMemo(() => {
    if (bubbles.length === 0)
      return { ok: false, msg: "吹き出しを 1 つ以上追加してください。" };
    for (const b of bubbles) {
      if (b.message_type === "text" && !b.text.trim()) {
        return { ok: false, msg: "テキスト吹き出しに本文を入力してください。" };
      }
      if (
        (b.message_type === "image" || b.message_type === "video") &&
        !b.s3_url
      ) {
        return {
          ok: false,
          msg: "画像/動画吹き出しはファイルをアップロードしてください。",
        };
      }
    }
    if (targetType === TARGET_TYPES.STUDIO && targetStudios.length === 0) {
      return { ok: false, msg: "対象の店舗を 1 つ以上選択してください。" };
    }
    if (targetType === TARGET_TYPES.CUSTOM && targetCustomers.length === 0) {
      return { ok: false, msg: "対象の顧客を 1 名以上選択してください。" };
    }
    if (
      targetType === TARGET_TYPES.CUSTOM &&
      targetCustomers.length > CUSTOM_TARGET_LIMIT
    ) {
      return {
        ok: false,
        msg: `顧客の選択は最大 ${CUSTOM_TARGET_LIMIT} 名までです。`,
      };
    }
    if (targetType === TARGET_TYPES.RECITAL2027) {
      if (recitalTargets.loading) {
        return { ok: false, msg: "発表会2027の対象者を確認中です…" };
      }
      if (recitalTargets.error) {
        return { ok: false, msg: recitalTargets.error };
      }
      if (recitalTargets.count === 0) {
        return { ok: false, msg: "発表会2027の配信対象が 0 名です。" };
      }
      if (recitalTargets.count > RECITAL_TARGET_LIMIT) {
        return {
          ok: false,
          msg: `配信対象が ${recitalTargets.count} 名で上限 ${RECITAL_TARGET_LIMIT} 名を超えています。`,
        };
      }
    }
    return { ok: true };
  }, [bubbles, targetType, targetStudios, targetCustomers, recitalTargets]);

  const isAnyUploading = Object.values(uploading).some(Boolean);

  // ---- 送信 ---------------------------------------------------------
  // 送信 / 下書き保存 共通の payload 組み立て。
  const buildPayload = (isDraft) => {
    // 発表会2027 は target_type='recital2027' のまま送る（一覧で「発表会2027」
    // と表示するため）。配信対象IDは slash_admin で解決済みのものを
    // target_customers に入れる（slash-app 側は custom と同じ扱い）。
    const effectiveCustomers =
      targetType === TARGET_TYPES.RECITAL2027
        ? recitalTargets.ids
        : targetType === TARGET_TYPES.CUSTOM
        ? targetCustomers
        : [];
    return {
      target_type: targetType,
      target_studios: targetType === TARGET_TYPES.STUDIO ? targetStudios : [],
      target_customers: effectiveCustomers,
      is_draft: isDraft,
      bubbles: bubbles.map((b) => ({
        order: b.order,
        message_type: b.message_type,
        text: b.message_type === "text" ? b.text : "",
        s3_url:
          b.message_type === "image" || b.message_type === "video"
            ? b.s3_url
            : "",
        thumbnail_url:
          b.message_type === "video" ? b.thumbnail_url || "" : "",
      })),
    };
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        // 既存の下書き: 最新内容で更新してから送信（同一レコードを遷移）。
        await slashAppBroadcasts.update(editingId, buildPayload(false));
        await slashAppBroadcasts.send(editingId);
      } else {
        // 新規: 作成と同時に即時送信。
        await slashAppBroadcasts.create(buildPayload(false));
      }
      setConfirmOpen(false);
      navigate("/mypage/broadcast-message-list");
    } catch (e) {
      setError(e.message || "送信に失敗しました");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  // 下書き保存（送信せず保存）。確認ダイアログは挟まずそのまま保存する。
  const saveDraft = async () => {
    setSavingDraft(true);
    setError(null);
    try {
      if (editingId) {
        // 既存の下書きをその場で更新（同一レコードを保持）。
        await slashAppBroadcasts.update(editingId, buildPayload(true));
      } else {
        await slashAppBroadcasts.create(buildPayload(true));
      }
      navigate("/mypage/broadcast-message-list");
    } catch (e) {
      setError(e.message || "下書きの保存に失敗しました");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCancel = () => {
    if (dirty) setDiscardOpen(true);
    else navigate("/mypage/broadcast-message-list");
  };

  // 送信確認を開く。発表会2027 は取得済み(recitalTargets)を使い、その他は
  // ここで配信先プレビューを取得する。
  const openConfirm = () => {
    setConfirmOpen(true);
    if (targetType === TARGET_TYPES.RECITAL2027) return;
    const fetchPreview = async () => {
      setTargetPreview({ loading: true, recipients: [], count: 0, error: null });
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/get_broadcast_target_preview/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            target_type: targetType,
            target_studios:
              targetType === TARGET_TYPES.STUDIO ? targetStudios : [],
            target_customers:
              targetType === TARGET_TYPES.CUSTOM ? targetCustomers : [],
          }),
        });
        if (!res.ok) throw new Error("配信先の取得に失敗しました");
        const data = await res.json();
        setTargetPreview({
          loading: false,
          recipients: data.recipients || [],
          count: data.count || 0,
          error: null,
        });
      } catch (e) {
        setTargetPreview({
          loading: false,
          recipients: [],
          count: 0,
          error: e.message || "配信先の取得に失敗しました",
        });
      }
    };
    fetchPreview();
  };

  // 送信確認に表示する配信先データ（発表会2027 は専用取得、他は汎用プレビュー）。
  const previewData =
    targetType === TARGET_TYPES.RECITAL2027 ? recitalTargets : targetPreview;

  // 配信対象の要約（サイドパネル / 送信確認に表示）。
  const targetSummary = () => {
    switch (targetType) {
      case TARGET_TYPES.ALL:
        return "全顧客（アクティブ会員）";
      case TARGET_TYPES.STUDIO:
        return `店舗 ${targetStudios.length} 件のアクティブ顧客`;
      case TARGET_TYPES.CUSTOM:
        return `指定顧客 ${targetCustomers.length} 名`;
      case TARGET_TYPES.RECITAL2027:
        return `発表会2027 参加者 ${recitalTargets.count} 名`;
      default:
        return "";
    }
  };

  // ---- 配信チャネル切替タブ (会員 / インストラクター) --------------
  // 新規作成時のみ切替可能。編集時 (channelLocked) は非表示。
  const channelTabs = channelLocked ? null : (
    <Menu pointing secondary>
      <Menu.Item
        name="会員へ一斉送信"
        active={channel === "customer"}
        onClick={() => setChannel("customer")}
      />
      <Menu.Item
        name="インストラクターへお知らせ"
        active={channel === "instructor"}
        onClick={() => setChannel("instructor")}
      />
    </Menu>
  );

  // ---- インストラクター向けお知らせ (別系統の自己完結フォーム) -------
  if (channel === "instructor") {
    return (
      <div className="broadcast-message-edit">
        <div className="page-header">
          <Header as="h1" className="page-title">
            {editingId ? "お知らせを編集" : "インストラクターへお知らせ"}
          </Header>
        </div>
        {channelTabs}
        <Segment className="form-container">
          <InstructorAnnouncementForm
            editingId={editingId || null}
            onDone={() => navigate("/mypage/broadcast-message-list")}
            onCancel={() => navigate("/mypage/broadcast-message-list")}
          />
        </Segment>
      </div>
    );
  }

  return (
    <div className="broadcast-message-edit">
      <div className="page-header">
        <Header as="h1" className="page-title">
          {editingId ? "下書きを編集" : "新規一斉送信作成"}
        </Header>
      </div>

      {channelTabs}

      <Segment className="form-container">
        <Form>
          {/* ===== 配信対象 ===== */}
          <Header as="h3">配信対象</Header>
          <Menu pointing secondary>
            <Menu.Item
              name="全顧客"
              active={targetType === TARGET_TYPES.ALL}
              onClick={() => {
                setTargetType(TARGET_TYPES.ALL);
                setDirty(true);
              }}
            />
            <Menu.Item
              name="店舗で絞り込み"
              active={targetType === TARGET_TYPES.STUDIO}
              onClick={() => {
                setTargetType(TARGET_TYPES.STUDIO);
                setDirty(true);
              }}
            />
            <Menu.Item
              name="顧客を指定"
              active={targetType === TARGET_TYPES.CUSTOM}
              onClick={() => {
                setTargetType(TARGET_TYPES.CUSTOM);
                setDirty(true);
              }}
            />
            <Menu.Item
              name="発表会2027"
              active={targetType === TARGET_TYPES.RECITAL2027}
              onClick={() => {
                setTargetType(TARGET_TYPES.RECITAL2027);
                setDirty(true);
              }}
            />
          </Menu>

          {targetType === TARGET_TYPES.ALL && (
            <Message info>
              <Message.Header>全顧客に配信</Message.Header>
              <p>
                現在アクティブな顧客全員に配信されます。インストラクター宛てには
                送信されません。
              </p>
            </Message>
          )}

          {targetType === TARGET_TYPES.STUDIO && (
            <Segment className="studio-mode-panel">
              {studioLoadError ? (
                <Message error>{studioLoadError}</Message>
              ) : (
                <>
                  <p>
                    選択中: <strong>{targetStudios.length}</strong> 店舗 — 該当
                    スタジオのレッスンを含むプランを持つアクティブ顧客に配信されます。
                  </p>
                  <div className="studio-checkboxes">
                    {Object.entries(sortedStudios).map(([prefecture, list]) => (
                      <div key={prefecture} className="prefecture-group">
                        <Header as="h4">{prefecture}</Header>
                        <div className="studio-group">
                          {list.map((studio) => (
                            <Form.Checkbox
                              key={studio.id}
                              label={studio.name}
                              checked={targetStudios.includes(studio.id)}
                              onChange={() => toggleStudio(studio.id)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Segment>
          )}

          {targetType === TARGET_TYPES.CUSTOM && (
            <CustomerPicker
              value={targetCustomers}
              onChange={(ids) => {
                setTargetCustomers(ids);
                setDirty(true);
              }}
            />
          )}

          {targetType === TARGET_TYPES.RECITAL2027 && (
            <>
              {recitalTargets.error ? (
                <Message error>{recitalTargets.error}</Message>
              ) : (
                <Message info>
                  <Message.Header>発表会2027の参加者に配信</Message.Header>
                  <p>
                    発表会2027に参加申込済みの顧客に配信されます。追加の選択は不要です。
                    インストラクター宛てには送信されません。
                  </p>
                  <p>
                    配信対象:{" "}
                    <strong>
                      {recitalTargets.loading
                        ? "確認中…"
                        : `${recitalTargets.count} 名`}
                    </strong>
                  </p>
                  {recitalTargets.loaded &&
                    !recitalTargets.loading &&
                    recitalTargets.count === 0 && (
                      <p style={{ color: "#9f3a38" }}>
                        現在、配信対象の参加者がいません。
                      </p>
                    )}
                  {recitalTargets.count > RECITAL_TARGET_LIMIT && (
                    <p style={{ color: "#9f3a38" }}>
                      対象が上限 {RECITAL_TARGET_LIMIT} 名を超えています。
                    </p>
                  )}
                </Message>
              )}
            </>
          )}

          <Divider />

          {/* ===== 吹き出し ===== */}
          <Header as="h3">メッセージ内容</Header>
          {bubbles.map((bubble) => (
            <Segment key={bubble.key} className="message-segment">
              <div
                className="message-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <Dropdown
                  selection
                  options={MESSAGE_TYPE_OPTIONS}
                  value={bubble.message_type}
                  onChange={(_, data) =>
                    updateBubble(bubble.key, {
                      message_type: data.value,
                      // 種別変更で前の入力をクリアして整合性を保つ
                      text: "",
                      s3_url: "",
                      thumbnail_url: "",
                    })
                  }
                />
                {bubbles.length > 1 && (
                  <Button
                    icon="trash"
                    negative
                    size="mini"
                    onClick={() => removeBubble(bubble.key)}
                  />
                )}
              </div>

              {bubble.message_type === "text" && (
                <Form.Field>
                  <TextArea
                    placeholder="メッセージを入力してください"
                    value={bubble.text}
                    onChange={(e) =>
                      updateBubble(bubble.key, { text: e.target.value })
                    }
                    rows={3}
                  />
                </Form.Field>
              )}

              {bubble.message_type === "image" && (
                <Form.Field>
                  <Button
                    as="label"
                    primary
                    htmlFor={`file-${bubble.key}`}
                    type="button"
                    disabled={uploading[bubble.key]}
                  >
                    {uploading[bubble.key]
                      ? "アップロード中..."
                      : "画像をアップロード"}
                  </Button>
                  <input
                    type="file"
                    id={`file-${bubble.key}`}
                    hidden
                    accept="image/*"
                    onChange={(e) =>
                      handleUpload(bubble.key, e.target.files[0])
                    }
                  />
                  {bubble.s3_url && (
                    <div className="image-preview">
                      <img
                        src={bubble.s3_url}
                        alt="プレビュー"
                        style={{ maxWidth: 200, marginTop: 10 }}
                      />
                    </div>
                  )}
                </Form.Field>
              )}

              {bubble.message_type === "video" && (
                <Form.Field>
                  <Button
                    as="label"
                    primary
                    htmlFor={`file-${bubble.key}`}
                    type="button"
                    disabled={uploading[bubble.key]}
                  >
                    {uploading[bubble.key]
                      ? "アップロード中..."
                      : "動画をアップロード"}
                  </Button>
                  <input
                    type="file"
                    id={`file-${bubble.key}`}
                    hidden
                    accept="video/*"
                    onChange={(e) =>
                      handleUpload(bubble.key, e.target.files[0])
                    }
                  />
                  {bubble.s3_url && (
                    <div className="video-preview">
                      <video
                        src={bubble.s3_url}
                        controls
                        style={{ maxWidth: 200, marginTop: 10 }}
                      />
                    </div>
                  )}
                </Form.Field>
              )}
            </Segment>
          ))}

          <Button
            type="button"
            icon
            labelPosition="left"
            onClick={addBubble}
            disabled={isAnyUploading || bubbles.length >= 20}
          >
            <Icon name="plus" />
            吹き出しを追加
          </Button>

          <Divider />

          {/* ===== バリデーション / アクション ===== */}
          {!validation.ok && (
            <Message warning>{validation.msg}</Message>
          )}
          {error && (
            <Message negative>
              <Message.Header>送信エラー</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div className="form-actions">
            <Button type="button" className="bc-cancel" onClick={handleCancel}>
              キャンセル
            </Button>
            <Button
              type="button"
              className="bc-draft"
              onClick={saveDraft}
              loading={savingDraft}
              disabled={
                !validation.ok || isAnyUploading || submitting || savingDraft
              }
            >
              下書き保存
            </Button>
            <Button
              type="button"
              primary
              onClick={openConfirm}
              disabled={
                !validation.ok || isAnyUploading || submitting || savingDraft
              }
            >
              送信
            </Button>
          </div>
        </Form>
      </Segment>

      {/* ===== 送信確認モーダル ===== */}
      <Modal
        open={confirmOpen}
        onClose={() => !submitting && setConfirmOpen(false)}
        size="tiny"
      >
        <Modal.Header>送信確認</Modal.Header>
        <Modal.Content scrolling>
          <p>
            <strong>配信対象:</strong> {targetSummary()}
          </p>

          <div style={{ color: "#888", fontSize: 12, margin: "12px 0 6px" }}>
            配信先（{previewData.count}名）
          </div>
          {previewData.loading ? (
            <p style={{ color: "#888" }}>読み込み中...</p>
          ) : previewData.error ? (
            <Message negative size="small">
              {previewData.error}
            </Message>
          ) : previewData.recipients.length === 0 ? (
            <p style={{ color: "#888" }}>対象がいません</p>
          ) : (
            <div
              style={{
                maxHeight: 180,
                overflowY: "auto",
                border: "1px solid var(--border)",
                borderRadius: 8,
                background: "rgba(255,255,255,0.02)",
                padding: 8,
              }}
            >
              {previewData.recipients.map((r) => (
                <div
                  key={r.user_id}
                  style={{
                    padding: "4px 2px",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: "bold",
                      color: "var(--text)",
                    }}
                  >
                    {r.name || "(名前未登録)"}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-dim)",
                      fontFamily: "var(--font-mono)",
                      wordBreak: "break-all",
                    }}
                  >
                    {r.email || `ID:${r.user_id}`}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ color: "#888", fontSize: 12, margin: "12px 0 6px" }}>
            メッセージプレビュー
          </div>
          <div
            style={{
              background: "#ebebf0",
              borderRadius: 8,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {bubbles.map((b) => (
              <div
                key={b.key}
                style={{ display: "flex", justifyContent: "flex-start" }}
              >
                {b.message_type === "text" && (
                  <div
                    style={{
                      maxWidth: "78%",
                      background: "#fff",
                      color: "#1a1a1a",
                      borderRadius: 16,
                      padding: "8px 12px",
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {b.text}
                  </div>
                )}
                {b.message_type === "image" && b.s3_url && (
                  <img
                    src={b.s3_url}
                    alt=""
                    style={{ maxWidth: 220, borderRadius: 16 }}
                  />
                )}
                {b.message_type === "video" && b.s3_url && (
                  <video
                    src={b.s3_url}
                    controls
                    style={{ maxWidth: 220, borderRadius: 16 }}
                  />
                )}
              </div>
            ))}
          </div>

          <p style={{ marginTop: 14 }}>
            この内容を即時配信します。よろしいですか？
          </p>
          <p style={{ color: "#888" }}>
            送信後はメッセージの編集・削除はできません。
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            キャンセル
          </Button>
          <Button positive loading={submitting} onClick={submit}>
            送信する
          </Button>
        </Modal.Actions>
      </Modal>

      {/* ===== 破棄確認モーダル ===== */}
      <Modal
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        size="tiny"
      >
        <Modal.Header>変更を破棄</Modal.Header>
        <Modal.Content>
          <p>変更内容が保存されていません。破棄してよろしいですか？</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDiscardOpen(false)}>キャンセル</Button>
          <Button
            negative
            onClick={() => navigate("/mypage/broadcast-message-list")}
          >
            破棄する
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}

export default BroadcastMessageEditPage;
